// ============================================================
// PostureCamera.tsx — 即時攝影 + MediaPipe AI 姿勢偵測元件
// ============================================================

import React, {
    useRef,
    useEffect,
    useState,
    useCallback,
    FC,
} from 'react';
import {
    PoseLandmarker,
    FilesetResolver,
    DrawingUtils,
    NormalizedLandmark,
} from '@mediapipe/tasks-vision';
import { analyzePose } from 'utils/postureAnalyzer';
import {
    PostureAnalysisResult,
    PostureSessionPayload,
    SessionSnapshot,
    Staff,
} from 'types/posture';
import ScorePanel from './ScorePanel';
import IssueAlerts from './IssueAlerts';

interface PostureCameraProps {
    staff: Staff;
    onSessionComplete?: (payload: PostureSessionPayload) => void;
}

const VIDEO_WIDTH  = 1280;
const VIDEO_HEIGHT = 720;

const MEDIAPIPE_WASM_URL =
    'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.14/wasm';
const POSE_MODEL_URL =
    'https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_lite/float16/1/pose_landmarker_lite.task';

const PostureCamera: FC<PostureCameraProps> = ({ staff, onSessionComplete }) => {
    const videoRef          = useRef<HTMLVideoElement>(null);
    const canvasRef         = useRef<HTMLCanvasElement>(null);
    const poseLandmarkerRef = useRef<PoseLandmarker | null>(null);
    const animFrameRef      = useRef<number>(0);
    const isRecordingRef    = useRef<boolean>(false);
    const sessionDataRef    = useRef<SessionSnapshot[]>([]);
    const lastSnapshotTime  = useRef<number>(0);

    // ★ 修正 1：用 ref 持有 stream，確保 cleanup 一定能拿到
    const streamRef         = useRef<MediaStream | null>(null);

    const [analysis,      setAnalysis]      = useState<PostureAnalysisResult | null>(null);
    const [isLoading,     setIsLoading]     = useState<boolean>(true);
    const [isRecording,   setIsRecording]   = useState<boolean>(false);
    const [errorMsg,      setErrorMsg]      = useState<string>('');
    const [sessionCount,  setSessionCount]  = useState<number>(0);

    // ── 繪製參考線 ─────────────────────────────────────────
    function drawPostureGuidelines(
        ctx: CanvasRenderingContext2D,
        width: number,
        height: number
    ): void {
        const centerX = width / 2;
        ctx.save();
        ctx.setLineDash([10, 5]);
        ctx.strokeStyle = 'rgba(255, 255, 0, 0.4)';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(centerX, 0);
        ctx.lineTo(centerX, height);
        ctx.stroke();
        ctx.strokeStyle = 'rgba(0, 255, 200, 0.3)';
        ctx.beginPath();
        ctx.moveTo(0, height * 0.3);
        ctx.lineTo(width, height * 0.3);
        ctx.stroke();
        ctx.restore();
    }

    // ★ 修正 2：detectLoop 改成 useRef 持有，避免 useCallback 閉包問題
    const detectLoopRef = useRef<() => void>(() => {});

    detectLoopRef.current = (): void => {
        const video  = videoRef.current;
        const canvas = canvasRef.current;

        if (!poseLandmarkerRef.current || !video || !canvas) {
            animFrameRef.current = requestAnimationFrame(detectLoopRef.current);
            return;
        }

        // ★ 修正 3：確認 video 已有資料且尺寸正確
        if (video.readyState < 2) {
            animFrameRef.current = requestAnimationFrame(detectLoopRef.current);
            return;
        }

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // ★ 修正 4：確保 canvas 尺寸與 video 一致
        if (canvas.width !== video.videoWidth && video.videoWidth > 0) {
            canvas.width  = video.videoWidth;
            canvas.height = video.videoHeight;
        }

        const results = poseLandmarkerRef.current.detectForVideo(video, performance.now());

        ctx.clearRect(0, 0, canvas.width, canvas.height);

        if (results.landmarks && results.landmarks.length > 0) {
            const landmarks: NormalizedLandmark[] = results.landmarks[0];

            const drawUtils = new DrawingUtils(ctx);
            drawUtils.drawLandmarks(landmarks, {
                color: '#00FF88',
                lineWidth: 2,
                radius: 4,
            });
            drawUtils.drawConnectors(landmarks, PoseLandmarker.POSE_CONNECTIONS, {
                color: '#00AAFF',
                lineWidth: 2,
            });

            const result = analyzePose(landmarks);
            if (result) {
                setAnalysis(result);
                drawPostureGuidelines(ctx, canvas.width, canvas.height);

                if (isRecordingRef.current) {
                    const now = Date.now();
                    if (now - lastSnapshotTime.current >= 1000) {
                        sessionDataRef.current.push({
                            timestamp: now,
                            score: result.totalScore,
                            grade: result.grade,
                            issues: result.issues.map(i => i.type),
                        });
                        lastSnapshotTime.current = now;
                        setSessionCount(prev => prev + 1);
                    }
                }
            }
        }

        animFrameRef.current = requestAnimationFrame(detectLoopRef.current);
    };

    // ── 初始化 ────────────────────────────────────────────
    useEffect(() => {
        let cancelled = false;

        async function init(): Promise<void> {
            try {
                const vision = await FilesetResolver.forVisionTasks(MEDIAPIPE_WASM_URL);
                poseLandmarkerRef.current = await PoseLandmarker.createFromOptions(vision, {
                    baseOptions: {
                        modelAssetPath: POSE_MODEL_URL,
                        delegate: 'GPU',
                    },
                    runningMode: 'VIDEO',
                    numPoses: 1,
                    minPoseDetectionConfidence: 0.6,
                    minPosePresenceConfidence: 0.6,
                    minTrackingConfidence: 0.5,
                });

                if (cancelled) return;

                const stream = await navigator.mediaDevices.getUserMedia({
                    video: {
                        width:  { ideal: VIDEO_WIDTH },
                        height: { ideal: VIDEO_HEIGHT },
                        facingMode: 'user',
                    },
                });

                if (cancelled) {
                    // 組件已卸載，馬上關掉剛拿到的 stream
                    stream.getTracks().forEach(t => t.stop());
                    return;
                }

                // ★ 修正 5：stream 存進 ref，cleanup 一定能拿到
                streamRef.current = stream;

                if (!videoRef.current) return;
                videoRef.current.srcObject = stream;

                // ★ 修正 6：用 onloadedmetadata + play() 確保 video 真的播放
                videoRef.current.onloadedmetadata = () => {
                    videoRef.current?.play().then(() => {
                        detectLoopRef.current();
                    });
                };

                setIsLoading(false);
            } catch (err) {
                console.error('[PostureCamera] 初始化失敗：', err);
                setErrorMsg('無法載入 AI 模型或攝影機，請確認權限與網路');
                setIsLoading(false);
            }
        }

        init();

        // ★ 修正 7：cleanup 同時清 stream tracks + animFrame + poseLandmarker
        return () => {
            cancelled = true;
            cancelAnimationFrame(animFrameRef.current);

            streamRef.current?.getTracks().forEach(track => {
                track.stop();
            });
            streamRef.current = null;

            if (videoRef.current) {
                videoRef.current.srcObject = null;
            }

            poseLandmarkerRef.current?.close();
            poseLandmarkerRef.current = null;
        };
    }, []);

    // ── 錄製控制 ─────────────────────────────────────────
    function handleStartRecording(): void {
        sessionDataRef.current  = [];
        lastSnapshotTime.current = 0;
        isRecordingRef.current  = true;
        setIsRecording(true);
        setSessionCount(0);
    }

    async function handleSaveSession(): Promise<void> {
        isRecordingRef.current = false;
        setIsRecording(false);

        const snapshots = sessionDataRef.current;
        if (snapshots.length === 0) return;

        const avgScore = Math.round(
            snapshots.reduce((sum, s) => sum + s.score, 0) / snapshots.length
        );

        const payload: PostureSessionPayload = {
            staffId: staff.id,
            sessionData: snapshots,
            averageScore: avgScore,
            duration: snapshots.length,
        };

        try {
            await fetch('/api/posture/session', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            onSessionComplete?.(payload);
        } catch (err) {
            console.error('[PostureCamera] 提交失敗：', err);
            setErrorMsg('儲存失敗，請稍後再試');
        }
    }

    // ── 渲染 ──────────────────────────────────────────────
    if (errorMsg) {
        return (
            <div className="error-container">
                <p className="error-icon">⚠️</p>
                <p className="error-message">{errorMsg}</p>
            </div>
        );
    }

    return (
        <div className="posture-camera-container">
            {isLoading && (
                <div className="loading-overlay">
                    <div className="loading-spinner" />
                    <p>正在載入 AI 姿勢分析模型...</p>
                </div>
            )}

            <div className="video-wrapper" style={{ position: 'relative' }}>
                {/* 隱藏 video，MediaPipe 從此讀幀 */}
                <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    style={{ display: 'none' }}
                />

                {/* Canvas 顯示骨骼可視化 */}
                <canvas
                    ref={canvasRef}
                    width={VIDEO_WIDTH}
                    height={VIDEO_HEIGHT}
                    style={{ width: '100%', borderRadius: '12px', background: '#1a1a2e' }}
                />

                {isRecording && (
                    <div className="recording-badge">
                        <span className="recording-dot" />
                        {sessionCount}s
                    </div>
                )}
            </div>

            {analysis && (
                <div className="analysis-side-panel">
                    <ScorePanel analysis={analysis} />
                    <IssueAlerts issues={analysis.issues} />
                </div>
            )}

            <div className="camera-controls">
                <button
                    onClick={handleStartRecording}
                    disabled={isRecording || isLoading}
                    className="btn btn-record"
                >
                    🔴 開始評估錄製
                </button>
                <button
                    onClick={handleSaveSession}
                    disabled={!isRecording}
                    className="btn btn-save"
                >
                    💾 完成並保存報告
                </button>
            </div>
        </div>
    );
};

export default PostureCamera;
